import React, { useState } from 'react';
import {
  View, Text, StyleSheet, ScrollView, TouchableOpacity,
  Platform, Linking,
} from 'react-native';
import Svg, { Circle, Path } from 'react-native-svg';
import * as appConfig from '../../config/configManager';
import { useProfileStore } from '../../contexts/StoreContext';
import type { NavigationTab } from '../../types/navigation';
import type { ProfileConnectionDto } from '../../types/api/appProfile';
import { InAppBrowserModal } from '../InAppBrowserModal';

// ─── Types ────────────────────────────────────────────────────────────────────

type ResourceScope = 'Emergency' | 'National' | 'Local' | 'Provider';

interface ResourceEntry {
  name: string;
  description: string;
  phone?: string;
  website?: string;
  hours?: string;
  tags?: string[];
}

// ─── Emergency resources (first 4 + SAMHSA from prior National list) ────────

const EMERGENCY_RESOURCES: ResourceEntry[] = [
  {
    name: 'Poison Control Center',
    description: 'Immediate expert guidance for poisoning emergencies involving children or adults.',
    phone: '1-800-222-1222',
    website: 'https://www.poisoncontrol.org',
    hours: '24/7',
    tags: ['Emergency', 'Safety'],
  },
  {
    name: '988 Suicide & Crisis Lifeline',
    description: 'Free, confidential crisis support for people in emotional distress or suicidal crisis, including parents and caregivers under stress.',
    phone: '988',
    website: 'https://988lifeline.org',
    hours: '24/7',
    tags: ['Mental Health', 'Crisis'],
  },
  {
    name: 'Childhelp National Child Abuse Hotline',
    description: 'Crisis intervention, information, and referrals for child abuse victims, parents, and professionals.',
    phone: '1-800-422-4453',
    website: 'https://www.childhelp.org/hotline',
    hours: '24/7',
    tags: ['Child Safety', 'Abuse'],
  },
  {
    name: 'National Domestic Violence Hotline',
    description: 'Confidential support and safety planning for individuals affected by domestic violence.',
    phone: '1-800-799-7233',
    website: 'https://www.thehotline.org',
    hours: '24/7',
    tags: ['Safety', 'Family'],
  },
  {
    name: 'SAMHSA National Helpline',
    description: 'Free, confidential treatment referrals for mental health and substance use disorders.',
    phone: '1-800-662-4357',
    website: 'https://www.samhsa.gov/find-help/national-helpline',
    hours: '24/7',
    tags: ['Mental Health', 'Substance Use'],
  },
];

// ─── National resources (remaining 5, with ECTA replaced) ───────────────────

const NATIONAL_RESOURCES: ResourceEntry[] = [
  {
    name: 'Head Start Program Locator',
    description: 'Find federally funded Head Start and Early Head Start programs providing early education and family support.',
    website: 'https://eclkc.ohs.acf.hhs.gov/center-locator',
    tags: ['Early Education', 'Family Support'],
  },
  {
    name: 'Help Me Grow Affiliate Locator',
    description: 'Find Help Me Grow affiliate locations and participating communities across the country.',
    website: 'https://helpmegrownational.org/hmg_affiliates/',
    tags: ['Help Me Grow', 'Navigation'],
  },
  {
    name: 'Family Voices Affiliate and F2F Locator',
    description: 'Find family-led Family Voices affiliates and Family-to-Family Health Information Centers by state or territory.',
    website: 'https://familyvoices.org/findhelp/',
    tags: ['Family Support', 'Care Coordination'],
  },
  {
    name: 'National Autism Association Helpline',
    description: 'Support and resources for families affected by autism spectrum disorder.',
    phone: '1-877-622-2884',
    website: 'https://nationalautismassociation.org',
    hours: 'Business hours',
    tags: ['Autism', 'Special Needs'],
  },
  {
    name: 'AAP HealthyChildren.org',
    description: 'Trusted pediatric health and parenting information from the American Academy of Pediatrics.',
    website: 'https://www.healthychildren.org',
    tags: ['Pediatric Health', 'Parenting'],
  },
  {
    name: 'CDC Learn the Signs. Act Early.',
    description: 'Free developmental milestone tracking materials and information on early action for developmental concerns.',
    website: 'https://www.cdc.gov/act-early/milestones/index.html',
    tags: ['Milestones', 'Developmental'],
  },
];

// ─── Local and provider locator-focused resources ───────────────────────────

const LOCAL_RESOURCES: ResourceEntry[] = [
  {
    name: 'Find Your Local Health Department',
    description: 'NACCHO directory to locate local health departments and public health contacts in your area.',
    website: 'https://www.naccho.org/membership/lhd-directory',
    tags: ['Public Health', 'Local Services'],
  },
  {
    name: 'Find Your Local Food Bank',
    description: 'Feeding America locator to find nearby food banks and local hunger-relief organizations.',
    website: 'https://www.feedingamerica.org/find-your-local-foodbank',
    tags: ['Food Security', 'Basic Needs'],
  },
  {
    name: 'State Early Intervention Program Locator',
    description: 'CDC directory to locate your state or territory early intervention program for infants and toddlers with developmental delays or disabilities.',
    website: 'https://www.cdc.gov/act-early/early-intervention/contact-information-by-state.html',
    tags: ['Early Intervention', 'Developmental'],
  },
  {
    name: 'State Public Health Programs Locator',
    description: 'USAGov state health department finder for public health programs, vital records, immunization information, and local department contacts.',
    website: 'https://www.usa.gov/state-health',
    tags: ['Public Health', 'State Programs'],
  },
  {
    name: 'State CHIP and Medicaid Coverage Locator',
    description: 'InsureKidsNow state coverage pages to find CHIP and Medicaid program information for children and teens.',
    website: 'https://www.insurekidsnow.gov/coverage',
    tags: ['Health Insurance'],
  },
  {
    name: 'State Child Welfare Agency Contacts',
    description: 'Child Welfare Information Gateway directory for state and territory child welfare agencies and related contacts.',
    website: 'https://www.childwelfare.gov/resources/states-territories-tribes/related-organizations/',
    tags: ['Child Safety', 'Family Support'],
  },
];

const PROVIDER_RESOURCES: ResourceEntry[] = [
  // Provider cards are driven from active profile connections in render logic.
];

// ─── Component ────────────────────────────────────────────────────────────────

interface ResourceListViewProps {
  onTabChange?: (tab: NavigationTab) => void;
}

export const ResourceListView: React.FC<ResourceListViewProps> = ({ onTabChange }) => {
  const [browserVisible, setBrowserVisible] = useState(false);
  const [browserUrl, setBrowserUrl] = useState('');
  const [browserTitle, setBrowserTitle] = useState('');

  const profileStore = useProfileStore();
  const { getActiveProfile } = profileStore();
  const activeProfile = getActiveProfile();
  const providerResourcesFromConnections: ResourceEntry[] = (activeProfile?.connections ?? []).map((conn: ProfileConnectionDto, index: number) => ({
    name: conn.DName || `Profile Connection ${index + 1}`,
    description: `Type: ${conn.Type || 'Connection'} • Permissions: ${conn.Permissions || 'Not specified'}`,
    website: conn.Email ? `mailto:${conn.Email}` : undefined,
    tags: ['Profile Connection', conn.Type || 'Provider'],
  }));

  const [activeScope, setActiveScope] = useState<ResourceScope>('Emergency');

  const SCOPES: { key: ResourceScope; icon: string; label: string }[] = [
    { key: 'Emergency', icon: '🚨', label: 'Emergency' },
    { key: 'National', icon: '🌐︎', label: 'National' },
    { key: 'Local',    icon: '📍',  label: 'Local'    },
    { key: 'Provider', icon: '🩺', label: 'Provider' },
  ];

  const currentScopeMeta = SCOPES.find(s => s.key === activeScope) ?? SCOPES[0];
  const selectedScopeIndex = SCOPES.findIndex(s => s.key === activeScope);

  const handlePrevScope = () => {
    if (selectedScopeIndex > 0) {
      setActiveScope(SCOPES[selectedScopeIndex - 1].key);
    }
  };

  const handleNextScope = () => {
    if (selectedScopeIndex < SCOPES.length - 1) {
      setActiveScope(SCOPES[selectedScopeIndex + 1].key);
    }
  };

  const getPageHeader = (scope: ResourceScope): string => {
    if (scope === 'Emergency') return 'Emergency Hotlines';
    if (scope === 'National') return 'National Resources';
    if (scope === 'Local') return 'Local Resources';
    return 'Provider Resources';
  };

  const getScopeDescription = (scope: ResourceScope): string => {
    if (scope === 'Emergency') return 'Immediate support hotlines and urgent family safety resources';
    if (scope === 'National') return 'Trusted national organizations and family guidance resources';
    if (scope === 'Local') return 'Locator pages to connect with state and community programs';
    return 'Provider resources from your active profile connections';
  };

  const renderTabIcon = (scope: ResourceScope, isSelected: boolean) => {
    if (scope === 'National') {
      const stroke = isSelected ? '#ffffff' : '#1f2937';
      return (
        <Svg width={16} height={16} viewBox="0 0 16 16" style={styles.tabIconSvg}>
          <Circle cx="8" cy="8" r="6.25" stroke={stroke} strokeWidth="1.5" fill="none" />
          <Path d="M8 1.75C6.2 3.6 5.2 5.85 5.2 8c0 2.15 1 4.4 2.8 6.25" stroke={stroke} strokeWidth="1.2" fill="none" strokeLinecap="round" />
          <Path d="M8 1.75c1.8 1.85 2.8 4.1 2.8 6.25 0 2.15-1 4.4-2.8 6.25" stroke={stroke} strokeWidth="1.2" fill="none" strokeLinecap="round" />
          <Path d="M2.3 5.75h11.4" stroke={stroke} strokeWidth="1.2" fill="none" strokeLinecap="round" />
          <Path d="M2.3 10.25h11.4" stroke={stroke} strokeWidth="1.2" fill="none" strokeLinecap="round" />
        </Svg>
      );
    }

    const iconText = SCOPES.find(s => s.key === scope)?.icon ?? '';
    return <Text style={[styles.tabIcon, isSelected && styles.selectedTabIcon]}>{iconText}</Text>;
  };

  const handlePhone = (phone: string) => {
    const stripped = phone.replace(/[^0-9]/g, '');
    if (Platform.OS === 'web') {
      (globalThis as any).open?.(`tel:${stripped}`, '_self');
    } else {
      Linking.openURL(`tel:${stripped}`);
    }
  };

  const handleWeb = (url: string, title?: string) => {
    if (Platform.OS === 'web') {
      (globalThis as any).open?.(url, '_blank', 'noopener,noreferrer');
      return;
    }

    if (/^(mailto:|tel:|sms:|geo:|maps:|intent:)/i.test(url)) {
      Linking.openURL(url).catch(() => {});
      return;
    }

    setBrowserTitle(title || 'Website');
    setBrowserUrl(url);
    setBrowserVisible(true);
  };

  const renderResourceCard = (r: ResourceEntry, index: number) => (
    <View key={`${r.name}-${index}`} style={styles.card}>
      <Text style={styles.cardName}>{r.name}</Text>
      <Text style={styles.cardDesc}>{r.description}</Text>

      {r.hours && (
        <Text style={styles.cardHours}>🕐 {r.hours}</Text>
      )}

      {(r.tags ?? []).length > 0 && (
        <View style={styles.tagRow}>
          {r.tags!.map(tag => (
            <View key={tag} style={styles.tag}>
              <Text style={styles.tagText}>{tag}</Text>
            </View>
          ))}
        </View>
      )}

      <View style={styles.cardActions}>
        {r.phone && (
          <TouchableOpacity style={styles.btnPhone} onPress={() => handlePhone(r.phone!)}>
            <Text style={styles.btnPhoneText}>📞 {r.phone}</Text>
          </TouchableOpacity>
        )}
        {r.website && (
          <TouchableOpacity style={styles.btnWeb} onPress={() => handleWeb(r.website!, r.name)}>
            <Text style={styles.btnWebText}>🌐 Website</Text>
          </TouchableOpacity>
        )}
      </View>

    </View>
  );

  const currentResources =
    activeScope === 'Emergency' ? EMERGENCY_RESOURCES :
    activeScope === 'National' ? NATIONAL_RESOURCES :
    activeScope === 'Local' ? LOCAL_RESOURCES :
    providerResourcesFromConnections;

  return (
    <View style={[styles.backgroundContainer, { backgroundColor: appConfig.getAppBackgroundColor() }]}>
      <View style={styles.fixedDomainHeader}>
        <View style={{ maxWidth: appConfig.getHeaderWidthMax(), alignSelf: 'center', width: '100%' }}>
          <View style={styles.domainTitleRow}>
            <Text style={styles.domainTitle}>{currentScopeMeta.icon} {getPageHeader(activeScope)}</Text>
          </View>
          <View style={styles.navigationRow}>
            <View style={styles.leftNavigationControls} />
            <View style={styles.centerDescription}>
              <Text style={styles.domainDescription}>{getScopeDescription(activeScope)}</Text>
            </View>
            <View style={styles.rightNavigationControls}>
              <TouchableOpacity
                style={[styles.navButton, { backgroundColor: appConfig.getNavButtonColor() }, selectedScopeIndex === 0 && styles.navButtonDisabled]}
                onPress={handlePrevScope}
                disabled={selectedScopeIndex === 0}
              >
                <Text style={[styles.navButtonText, { fontSize: 20 }]}>◀</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.navButton, { backgroundColor: appConfig.getNavButtonColor() }, selectedScopeIndex === SCOPES.length - 1 && styles.navButtonDisabled]}
                onPress={handleNextScope}
                disabled={selectedScopeIndex === SCOPES.length - 1}
              >
                <Text style={[styles.navButtonText, { fontSize: 20 }]}>▶</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </View>

      <View style={styles.tabSectionOuter}>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={[styles.tabContainer, { maxWidth: appConfig.getHeaderWidthMax(), alignSelf: 'center', width: '100%' }]}
          contentContainerStyle={styles.tabContent}
        >
          {SCOPES.map(tab => (
            <TouchableOpacity
              key={tab.key}
              style={[styles.tab, activeScope === tab.key && { backgroundColor: appConfig.getNavButtonColor() }]}
              onPress={() => setActiveScope(tab.key)}
            >
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 5 }}>
                {renderTabIcon(tab.key, activeScope === tab.key)}
                <Text style={[styles.tabText, activeScope === tab.key && styles.selectedTabText]}>
                  {tab.label}
                </Text>
              </View>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>

      <View style={styles.cardWrapper}>
        <View style={[styles.container, { maxWidth: appConfig.getSingleCardMaxWidth(), width: '100%' }]}>
          <ScrollView style={styles.contentContainer} contentContainerStyle={{ paddingBottom: 24 }}>
            <View style={styles.section}>
              {currentResources.length > 0 ? (
                currentResources.map((r, i) => renderResourceCard(r, i))
              ) : (
                <View style={styles.emptyProviderState}>
                  <Text style={styles.emptyProviderTitle}>No provider resources available</Text>
                  <Text style={styles.emptyProviderBody}>
                    Add profile connections in Connections {'>'} Profile Connections to populate this tab.
                  </Text>
                </View>
              )}
              {activeScope === 'Emergency' && (
                <View style={styles.footer}>
                  <Text style={styles.footerText}>In an emergency, always call 911.</Text>
                </View>
              )}
            </View>
          </ScrollView>
        </View>
      </View>

      <InAppBrowserModal
        visible={browserVisible}
        url={browserUrl}
        title={browserTitle}
        onClose={() => setBrowserVisible(false)}
      />
    </View>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  backgroundContainer: {
    flex: 1,
    backgroundColor: '#3b82f6',
  },
  fixedDomainHeader: {
    backgroundColor: appConfig.getHeaderBackgroundColor(),
    paddingTop: 16 + appConfig.getMobileHeaderTopPadding(),
    paddingBottom: 16,
    paddingHorizontal: 24,
  },
  domainTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  domainTitle: {
    fontSize: 22,
    fontWeight: 'bold',
    color: '#ffffff',
    flex: 1,
    marginBottom: 4,
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 0, height: 2 },
    textShadowRadius: 4,
  },
  navigationRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  leftNavigationControls: {
    flexDirection: 'row',
    gap: 8,
    minWidth: 36,
  },
  rightNavigationControls: {
    flexDirection: 'row',
    gap: 8,
  },
  centerDescription: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 8,
  },
  domainDescription: {
    fontSize: 13,
    color: '#e2e8f0',
    textAlign: 'center',
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 2,
  },
  navButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: '#3498db',
    justifyContent: 'center',
    alignItems: 'center',
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.2,
    shadowRadius: 2,
  },
  navButtonDisabled: {
    backgroundColor: '#bdc3c7',
    opacity: 0.6,
  },
  navButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: 'bold',
  },
  tabSectionOuter: {
    backgroundColor: appConfig.getHeaderBackgroundColor(),
  },
  tabContainer: {
    maxHeight: 60,
  },
  tabContent: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    flexGrow: 1,
    justifyContent: 'center',
  },
  tab: {
    paddingHorizontal: 20,
    paddingVertical: 12,
    marginRight: 8,
    borderRadius: 20,
    backgroundColor: '#f1f3f4',
    minWidth: 100,
    alignItems: 'center',
  },
  tabText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#5f6368',
  },
  tabIcon: {
    fontSize: 15,
    color: '#374151',
  },
  tabIconSvg: {
    marginTop: 1,
  },
  selectedTabIcon: {
    color: '#ffffff',
  },
  selectedTabText: {
    color: '#ffffff',
  },
  cardWrapper: {
    flex: 1,
    paddingHorizontal: appConfig.getCardMargin(),
    paddingTop: 12,
    alignItems: 'center',
  },
  container: {
    flex: 1,
    backgroundColor: '#f8f9fa',
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    overflow: 'hidden',
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  contentContainer: {
    flex: 1,
  },
  section: {
    margin: 16,
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    borderLeftWidth: 4,
    borderLeftColor: '#9b59b6',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  cardName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#2c3e50',
    marginBottom: 4,
  },
  cardDesc: {
    fontSize: 14,
    color: '#7f8c8d',
    lineHeight: 20,
    marginBottom: 8,
  },
  cardHours: {
    fontSize: 12,
    color: '#9b59b6',
    fontWeight: '500',
    marginBottom: 6,
  },
  tagRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
    marginBottom: 10,
  },
  tag: {
    backgroundColor: '#f3e8ff',
    borderRadius: 20,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  tagText: {
    fontSize: 11,
    color: '#6b21a8',
    fontWeight: '600',
  },
  cardActions: {
    flexDirection: 'row',
    gap: 8,
    flexWrap: 'wrap',
  },
  btnPhone: {
    backgroundColor: '#eaf4fb',
    borderWidth: 1,
    borderColor: '#3498db',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  btnPhoneText: {
    fontSize: 13,
    color: '#2c3e50',
    fontWeight: '600',
  },
  btnWeb: {
    backgroundColor: '#f8f9fa',
    borderWidth: 1,
    borderColor: '#9b59b6',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  btnWebText: {
    fontSize: 13,
    color: '#5b21b6',
    fontWeight: '600',
  },
  footer: {
    marginTop: 6,
    backgroundColor: '#eaf4fb',
    padding: 12,
    borderRadius: 10,
    borderLeftWidth: 4,
    borderLeftColor: '#3498db',
  },
  footerText: {
    fontSize: 13,
    color: '#34495e',
    lineHeight: 20,
  },
  emptyProviderState: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
    borderColor: '#dbeafe',
    marginBottom: 12,
  },
  emptyProviderTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#2c3e50',
    marginBottom: 6,
  },
  emptyProviderBody: {
    fontSize: 14,
    color: '#7f8c8d',
    lineHeight: 20,
  },
});
